/**
 * klaviyo-custom.js
 * Custom Back-In-Stock (BIS) behaviour on top of Klaviyo's native "backinstock"
 * widget. Loads AFTER the inline config in theme.liquid, which:
 *   - calls klaviyo.init(...)
 *   - builds the `vosKlaviyoBisConfig` object (from theme settings)
 *   - calls klaviyo.enable("backinstock", vosKlaviyoBisConfig)
 *   - exposes window.vosKlaviyoBis = { config, apiHostname }
 *
 * Everything in this file is our customisation layer. It reads the config that
 * theme.liquid set up and then:
 *   1. Adds a custom "Notify Me When Available" button to out-of-stock Rebuy
 *      recommendation cards (injectRebuyBis).
 *   2. Replaces the Searchanise "View product" button with the notify-me button
 *      on out-of-stock search-result cards (injectSnizeBis).
 *   3. Opens a custom modal (email + optional SMS, E.164 validation, Klaviyo
 *      client API) when any .klaviyo-bis-trigger is clicked.
 *
 * All styling lives in assets/klaviyo-custom.css (loaded via <link> in
 * theme.liquid), NOT injected at runtime.
 *
 * IMPORTANT (2024): The Klaviyo init/enable block lives in theme.liquid, NOT
 * here. Moving it here broke everything because the asset executed before the
 * Klaviyo loader/global was guaranteed to be ready, so the whole file threw and
 * NO notify-me button worked. Only customisations live here now.
 */
(function () {
  'use strict';

  /** Returns the config + API hostname set up by the inline script in theme.liquid. */
  function cfg() { return window.vosKlaviyoBis || { config: {} }; }

  /**
   * Resolves the public Klaviyo company/account ID used to build the client API
   * URLs. Prefers the config (theme.liquid now sets `account`), then falls back
   * to the Klaviyo global's _klaviyo/_data array as a safety net so the
   * company_id in the fetch URLs is never "undefined".
   */
  function companyId() {
    var id = cfg().config.account;
    if (id) return id;
    try {
      var k = window._klaviyo || window.klaviyo;
      if (k && Array.isArray(k._data)) {
        for (var i = 0; i < k._data.length; i++) {
          if (k._data[i] && k._data[i][0] === 'identify') {
            var company = k._data[i][1] && k._data[i][1].$company_id;
            if (company) return company;
          }
        }
      }
    } catch (e) {}
    return '';
  }

  /**
   * Sets the visible text of an injected trigger without wiping out any SVG
   * icon child it may already contain.
   */
  function setButtonText(el, text) {
    var svg = el.querySelector('svg');
    el.textContent = '';
    if (svg) el.appendChild(svg);
    el.appendChild(document.createTextNode(text));
  }

  /**
   * Applies the configured collection/search button label to every
   * .klaviyo-bis-trigger that carries a data-klaviyo-handle. Used after
   * injectRebuyBis / injectSnizeBis so injected buttons show the right copy.
   */
  function applyCollectionButtonText() {
    var c = cfg().config;
    var text = (c.trigger && c.trigger.collection_page_text) || 'Notify Me When Available';
    var els = document.querySelectorAll('.klaviyo-bis-trigger[data-klaviyo-handle]');
    for (var i = 0; i < els.length; i++) {
      setButtonText(els[i], text);
    }
  }

  /**
   * Applies the configured product-page label to product-page triggers
   * (those carrying data-vos-bis-pdp).
   */
  function applyProductButtonText() {
    var c = cfg().config;
    var text = (c.trigger && c.trigger.product_page_text) || 'Notify Me';
    var els = document.querySelectorAll('.klaviyo-bis-trigger[data-klaviyo-handle][data-vos-bis-pdp]');
    for (var i = 0; i < els.length; i++) {
      setButtonText(els[i], text);
    }
  }

  /** Extracts the product handle from a /products/<handle> style href. */
  function extractHandleFromHref(href) {
    if (!href) return null;
    var match = href.match(/\/products\/([^\/\?]+)/);
    return match ? match[1] : null;
  }

  /** True on the Searchanise search-results page(s). */
  function isSearchPage() {
    var path = window.location.pathname || '';
    // Searchanise renders full results on /pages/search-results-page (the
    // store's configured ResultsFormPath), plus classic /search?... URLs.
    if (path.search(/search-results-page/) !== -1) return true;
    return path.search(/\/search(\/|$)/) !== -1;
  }

  /** Root container Searchanise renders search results into. */
  function findSnizeRoot() {
    return document.getElementById('snize_results');
  }

  /**
   * SEARCH RESULTS CUSTOMISATION:
   * Replaces the Searchanise "View product" button with the notify-me trigger on
   * out-of-stock search cards. Scoped to the /search page and to the #snize_results
   * container so Snize-powered collection PLPs are left untouched.
   *
   * Each out-of-stock card (.snize-product.snize-product-out-of-stock) gets a
   * `data-vos-bis-injected="1"` marker so we never double-inject across re-renders
   * (pagination, sorting, filter changes). The handle is pulled from the card's
   * .snize-view-link href.
   */
  function injectSnizeBis() {
    if (!isSearchPage()) return;
    var root = findSnizeRoot();
    if (!root) return;
    var cards = root.querySelectorAll('.snize-product.snize-product-out-of-stock');
    for (var i = 0; i < cards.length; i++) {
      var card = cards[i];
      if (card.getAttribute('data-vos-bis-injected') === '1') continue;
      var viewLink = card.querySelector('.snize-view-link');
      var handle = viewLink ? extractHandleFromHref(viewLink.getAttribute('href')) : null;
      if (!handle) {
        var allLinks = card.querySelectorAll('a[href*="/products/"]');
        for (var j = 0; j < allLinks.length; j++) {
          handle = extractHandleFromHref(allLinks[j].getAttribute('href'));
          if (handle) break;
        }
      }
      if (!handle) continue;
      var btn = document.createElement('a');
      btn.className = 'klaviyo-bis-trigger snize-bis-trigger';
      btn.href = '#';
      btn.setAttribute('data-klaviyo-handle', handle);
      // The Snize "View product" CTA in this store's Big Pictures template is a
      // .snize-button element. Prefer replacing it so the OOS card shows the
      // notify-me trigger in its place; fall back to a buttons container, then
      // to appending inside the card's overlay.
      var viewBtn =
        card.querySelector('.snize-button') ||
        card.querySelector('.snize-view-product-button');
      var buttonsEl =
        card.querySelector('.snize-buttons') ||
        card.querySelector('.snize-overhidden');
      if (viewBtn && viewBtn.parentNode) {
        viewBtn.parentNode.replaceChild(btn, viewBtn);
      } else if (buttonsEl) {
        buttonsEl.appendChild(btn);
      } else {
        card.appendChild(btn);
      }
      card.setAttribute('data-vos-bis-injected', '1');
    }
    applyCollectionButtonText();
  }

  /**
   * REBUY RECOMMENDATIONS CUSTOMISATION:
   * Adds the notify-me trigger to out-of-stock Rebuy recommendation cards
   * (the .rebuy-product-block rendered by the Rebuy engine). Uses the same
   * data-vos-bis-injected guard as the Searchanise injection.
   */
  function injectRebuyBis() {
    var containers = document.querySelectorAll('[data-rebuy-id]');
    for (var c = 0; c < containers.length; c++) {
      var cards = containers[c].querySelectorAll('.rebuy-product-block');
      for (var i = 0; i < cards.length; i++) {
        var card = cards[i];
        var handle = null;
        var titleLink = card.querySelector('.rebuy-product-title');
        if (titleLink) handle = extractHandleFromHref(titleLink.getAttribute('href'));
        if (!handle) {
          var allLinks = card.querySelectorAll('a[href*="/products/"]');
          for (var j = 0; j < allLinks.length; j++) {
            handle = extractHandleFromHref(allLinks[j].getAttribute('href'));
            if (handle) break;
          }
        }
        if (!handle) continue;

        // Cheap path: an already-resolved, fully-OOS card that lost its notify
        // button during a Rebuy re-render. CSS keeps the native ATC hidden via
        // the vos-rebuy-oos class, so we only need to re-add the trigger.
        if (card.getAttribute('data-vos-bis-injected') === '1') {
          if (card.classList.contains('vos-rebuy-oos') && !card.querySelector('.rebuy-bis-trigger')) {
            var reBtn = document.createElement('a');
            reBtn.className = 'klaviyo-bis-trigger rebuy-bis-trigger';
            reBtn.href = '#';
            reBtn.setAttribute('data-klaviyo-handle', handle);
            (card.querySelector('.rebuy-product-actions') || card).appendChild(reBtn);
            applyCollectionButtonText();
          }
          continue;
        }

        // Mark processed immediately so we never re-scan/re-fetch this card.
        card.setAttribute('data-vos-bis-injected', '1');
        (function (card, handle) {
          fetch(productDataUrl(handle), { headers: { 'Accept': 'application/json' } })
            .then(function (r) { return r.json(); })
            .then(function (p) {
              if (!p || !p.variants || p.variants.length === 0) return;
              // "Out of stock" = EVERY variant unavailable. If any variant is
              // available the card is in stock and must show only the native ATC.
              var allOos = p.variants.every(function (v) { return v.available === false; });
              var actionsEl = card.querySelector('.rebuy-product-actions');
              if (allOos) {
                // Hide the native ATC button / variant selector, then show only
                // the notify-me trigger in its place.
                card.classList.add('vos-rebuy-oos');
                var btn = card.querySelector('.rebuy-bis-trigger');
                if (!btn) {
                  btn = document.createElement('a');
                  btn.className = 'klaviyo-bis-trigger rebuy-bis-trigger';
                  btn.href = '#';
                  btn.setAttribute('data-klaviyo-handle', handle);
                  (actionsEl || card).appendChild(btn);
                }
                applyCollectionButtonText();
              } else {
                // In stock: no notify-me, and never hide the native ATC controls.
                card.classList.remove('vos-rebuy-oos');
                var staleBtn = card.querySelector('.rebuy-bis-trigger');
                if (staleBtn && staleBtn.parentNode) staleBtn.parentNode.removeChild(staleBtn);
              }
            })
            .catch(function () {});
        })(card, handle);
      }
    }
  }

  /** URL of the /products/<handle>.js JSON endpoint used to read variants. */
  function productDataUrl(handle) {
    return window.shopUrl + '/products/' + handle + '.js';
  }

  /** ISO country code -> dial code table used by the SMS field. */
  var BIS_COUNTRIES = [
    ['AF','93'],['AX','358'],['AL','355'],['DZ','213'],['AS','1684'],['AD','376'],['AO','244'],['AI','1264'],['AQ','672'],['AG','1268'],['AR','54'],['AM','374'],['AW','297'],['AU','61'],['AT','43'],['AZ','994'],['BS','1242'],['BH','973'],['BD','880'],['BB','1246'],['BY','375'],['BE','32'],['BZ','501'],['BJ','229'],['BM','1441'],['BT','975'],['BO','591'],['BA','387'],['BW','267'],['BR','55'],['IO','246'],['BN','673'],['BG','359'],['BF','226'],['BI','257'],['KH','855'],['CM','237'],['CA','1'],['CV','238'],['KY','345'],['CF','236'],['TD','235'],['CL','56'],['CN','86'],['CX','61'],['CC','61'],['CO','57'],['KM','269'],['CG','242'],['CD','243'],['CK','682'],['CR','506'],['CI','225'],['HR','385'],['CU','53'],['CY','357'],['CZ','420'],['DK','45'],['DJ','253'],['DM','1767'],['DO','1849'],['EC','593'],['EG','20'],['SV','503'],['GQ','240'],['ER','291'],['EE','372'],['ET','251'],['FK','500'],['FO','298'],['FJ','679'],['FI','358'],['FR','33'],['GF','594'],['PF','689'],['GA','241'],['GM','220'],['GE','995'],['DE','49'],['GH','233'],['GI','350'],['GR','30'],['GL','299'],['GD','1473'],['GP','590'],['GU','1671'],['GT','502'],['GG','44'],['GN','224'],['GW','245'],['GY','595'],['HT','509'],['VA','379'],['HN','504'],['HK','852'],['HU','36'],['IS','354'],['IN','91'],['ID','62'],['IR','98'],['IQ','964'],['IE','353'],['IM','44'],['IL','972'],['IT','39'],['JM','1876'],['JP','81'],['JE','44'],['JO','962'],['KZ','77'],['KE','254'],['KI','686'],['KP','850'],['KR','82'],['KW','965'],['KG','996'],['LA','856'],['LV','371'],['LB','961'],['LS','266'],['LR','231'],['LY','218'],['LI','423'],['LT','370'],['LU','352'],['MO','853'],['MK','389'],['MG','261'],['MW','265'],['MY','60'],['MV','960'],['ML','223'],['MT','356'],['MH','692'],['MQ','596'],['MR','222'],['MU','230'],['YT','262'],['MX','52'],['FM','691'],['MD','373'],['MC','377'],['MN','976'],['ME','382'],['MS','1664'],['MA','212'],['MZ','258'],['MM','95'],['NA','264'],['NR','674'],['NP','977'],['NL','31'],['AN','599'],['NC','687'],['NZ','64'],['NI','505'],['NE','227'],['NG','234'],['NU','683'],['NF','672'],['MP','1670'],['NO','47'],['OM','968'],['PK','92'],['PW','680'],['PS','970'],['PA','507'],['PG','675'],['PY','595'],['PE','51'],['PH','63'],['PN','64'],['PL','48'],['PT','351'],['PR','1787'],['QA','974'],['RO','40'],['RU','7'],['RW','250'],['SH','290'],['KN','1869'],['LC','1758'],['PM','508'],['VC','1784'],['WS','685'],['SM','378'],['ST','239'],['SA','966'],['SN','221'],['RS','381'],['SC','248'],['SL','232'],['SG','65'],['SK','421'],['SI','386'],['SB','677'],['SO','252'],['ZA','27'],['GS','500'],['ES','34'],['LK','94'],['SD','249'],['SR','597'],['SJ','47'],['SZ','268'],['SE','46'],['CH','41'],['SY','963'],['TW','886'],['TJ','992'],['TZ','255'],['TH','66'],['TG','228'],['TK','690'],['TO','676'],['TT','1868'],['TN','216'],['TR','90'],['TM','993'],['TC','1649'],['TV','688'],['UG','256'],['UA','380'],['AE','971'],['GB','44'],['US','1'],['UM','1'],['UY','598'],['UZ','998'],['VU','678'],['VE','58'],['VN','84'],['VG','1284'],['VI','1340'],['WF','681'],['EH','212'],['YE','967'],['ZM','260'],['ZW','263']
  ];

  /**
   * CUSTOM MODAL:
   * Renders the notify-me modal (email + optional SMS) entirely client-side and
   * submits to the Klaviyo client API. Key customisations versus the stock
   * Klaviyo backinstock widget:
   *   - Email OR phone contact channel.
   *   - SMS enabled/disabled toggle from config (sms_enabled).
   *   - E.164 phone normalisation (dial code + digits).
   *   - require_one_channel validation: must supply email and/or phone.
   *   - When phone is supplied and an SMS consent list is configured, a separate
   *     /client/subscriptions/ call captures SMS consent before the BIS call.
   */
  function openModal(c) {
    var modal = (c.modal || {});
    var textColor = modal.text_color || '#222';
    var buttonBg = modal.button_background_color || '#3d1152';
    var buttonText = modal.button_text_color || '#fff';
    var btnLabel = modal.button_label || 'Notify Me When Available';
    var requireOne = modal.require_one_channel !== false;
    var defaultCountry = modal.default_country || 'US';
    var smsEnabled = modal.sms_enabled !== false;

    var closeSvg =
      '<svg width="30" height="30" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg" class="vos-bis-close-svg">' +
        '<path d="M25.7071 14.2929C26.0976 14.6834 26.0976 15.3166 25.7071 15.7071L21.4142 20L25.7071 24.2929C26.0976 24.6834 26.0976 25.3166 25.7071 25.7071C25.3166 26.0976 24.6834 26.0976 24.2929 25.7071L20 21.4142L15.7071 25.7071C15.3166 26.0976 14.6834 26.0976 14.2929 25.7071C13.9024 25.3166 13.9024 24.6834 14.2929 24.2929L18.5858 20L14.2929 15.7071C13.9024 15.3166 13.9024 14.6834 14.2929 14.2929C14.6834 13.9024 15.3166 13.9024 15.7071 14.2929L20 18.5858L24.2929 14.2929C24.6834 13.9024 25.3166 13.9024 25.7071 14.2929Z" fill="#4A4A4A"></path>' +
        '<path fill-rule="evenodd" clip-rule="evenodd" d="M20 34C27.732 34 34 27.732 34 20C34 12.268 27.732 6 20 6C12.268 6 6 12.268 6 20C6 27.732 12.268 34 20 34ZM20 32C26.6274 32 32 26.6274 32 20C32 13.3726 26.6274 8 20 8C13.3726 8 8 13.3726 8 20C8 26.6274 13.3726 32 20 32Z" fill="#4A4A4A"></path>' +
      '</svg>';

    var emailIcon =
      '<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" class="vos-bis-channel-icon">' +
        '<path d="M14.25 4.5C15.7688 4.5 17 5.73122 17 7.25V12.75C17 14.2688 15.7688 15.5 14.25 15.5H5.75C4.23122 15.5 3 14.2688 3 12.75V7.25C3 5.73122 4.23122 4.5 5.75 4.5H14.25ZM5.75 6C5.05964 6 4.5 6.55964 4.5 7.25V12.75C4.5 13.4404 5.05964 14 5.75 14H14.25C14.9404 14 15.5 13.4404 15.5 12.75V7.25C15.5 6.55964 14.9404 6 14.25 6H5.75ZM13.4326 7.57031C13.8079 7.39517 14.2545 7.55738 14.4297 7.93262C14.6048 8.30792 14.4426 8.75447 14.0674 8.92969L10.5283 10.5811C10.1934 10.7372 9.80659 10.7372 9.47168 10.5811L5.93262 8.92969C5.55738 8.75447 5.39517 8.30792 5.57031 7.93262C5.74553 7.55738 6.19208 7.39517 6.56738 7.57031L10 9.17188L13.4326 7.57031Z" fill="#4A4A4A"></path>' +
      '</svg>';
    var smsIcon =
      '<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" class="vos-bis-channel-icon">' +
        '<path d="M6 8C6 7.58579 6.33579 7.25 6.75 7.25H12.25C12.6642 7.25 13 7.58579 13 8C13 8.41421 12.6642 8.75 12.25 8.75H6.75C6.33579 8.75 6 8.41421 6 8Z" fill="#4A4A4A"></path>' +
        '<path d="M6.75 9.5C6.33579 9.5 6 9.83579 6 10.25C6 10.6642 6.33579 11 6.75 11H10.25C10.6642 11 11 10.6642 11 10.25C11 9.83579 10.6642 9.5 10.25 9.5H6.75Z" fill="#4A4A4A"></path>' +
        '<path fill-rule="evenodd" clip-rule="evenodd" d="M3 8.75C3 6.12665 5.12665 4 7.75 4H12.25C14.8734 4 17 6.12665 17 8.75V9.5C17 12.1234 14.8734 14.25 12.25 14.25H10.6241L7.44628 16.6028C7.21864 16.7713 6.91547 16.7972 6.66252 16.6698C6.40957 16.5423 6.25 16.2832 6.25 16V14.0081C4.36171 13.38 3 11.5997 3 9.5V8.75ZM7.75 5.5C5.95507 5.5 4.5 6.95507 4.5 8.75V9.5C4.5 11.0858 5.63655 12.4078 7.13978 12.6929C7.49378 12.7601 7.75 13.0695 7.75 13.4298V14.5115L9.93034 12.8972C10.0595 12.8016 10.2159 12.75 10.3766 12.75H12.25C14.0449 12.75 15.5 11.2949 15.5 9.5V8.75C15.5 6.95507 14.0449 5.5 12.25 5.5H7.75Z" fill="#4A4A4A"></path>' +
      '</svg>';

    var countryOptions = '';
    for (var ci = 0; ci < BIS_COUNTRIES.length; ci++) {
      var code = BIS_COUNTRIES[ci][0];
      var dial = BIS_COUNTRIES[ci][1];
      countryOptions += '<option value="' + dial + '"' + (code === defaultCountry ? ' selected' : '') + '>' + code + ' (+' + dial + ')</option>';
    }

    var overlay = document.createElement('div');
    overlay.className = 'vos-bis-overlay';
    overlay.innerHTML =
      '<div class="vos-bis-modal" role="dialog" aria-modal="true">' +
        '<button type="button" class="vos-bis-close" aria-label="Close">' + closeSvg + '</button>' +
        '<h3 class="vos-bis-title"></h3>' +
        '<p class="vos-bis-body"></p>' +
        '<form class="vos-bis-form">' +
          (c.variants && c.variants.length > 1 ? '<label class="vos-bis-label"></label><select class="vos-bis-variant"></select>' : '') +
          '<div class="vos-bis-channel">' +
            '<label class="vos-bis-channel-label">' + emailIcon + '<span class="vos-bis-channel-text"></span></label>' +
            '<input type="email" class="vos-bis-email" placeholder="" />' +
          '</div>' +
          (smsEnabled ? '<div class="vos-bis-channel">' +
            '<label class="vos-bis-channel-label">' + smsIcon + '<span class="vos-bis-channel-text"></span></label>' +
            '<div class="vos-bis-sms-row">' +
              '<select class="vos-bis-country">' + countryOptions + '</select>' +
              '<input type="tel" class="vos-bis-phone" placeholder="" />' +
            '</div>' +
          '</div>' : '') +
          '<div class="vos-bis-msg vos-bis-error" style="display:none"></div>' +
          '<div class="vos-bis-msg vos-bis-success" style="display:none"></div>' +
          '<button type="submit" class="vos-bis-submit"></button>' +
        '</form>' +
      '</div>';
    document.body.appendChild(overlay);

    var title = overlay.querySelector('.vos-bis-title');
    var body = overlay.querySelector('.vos-bis-body');
    var channelLabels = overlay.querySelectorAll('.vos-bis-channel-text');
    var emailInput = overlay.querySelector('.vos-bis-email');
    var phoneInput = overlay.querySelector('.vos-bis-phone');
    var countrySel = overlay.querySelector('.vos-bis-country');
    var submit = overlay.querySelector('.vos-bis-submit');
    var errorMsg = overlay.querySelector('.vos-bis-error');
    var successMsg = overlay.querySelector('.vos-bis-success');
    var close = overlay.querySelector('.vos-bis-close');
    var variantSel = overlay.querySelector('.vos-bis-variant');

    title.textContent = (modal.title || 'Notify me via:');
    body.textContent = (modal.body_content || '').replace('{product_name}', c.title);
    channelLabels[0].textContent = (modal.email_field_label || 'Email') + ':';
    if (smsEnabled && channelLabels[1]) channelLabels[1].textContent = (modal.sms_field_label || 'Sms') + ':';
    emailInput.placeholder = modal.email_placeholder || 'Email Address';
    if (phoneInput) phoneInput.placeholder = modal.sms_phone_placeholder || 'Phone Number';
    submit.textContent = btnLabel;
    if (variantSel) {
      var variantLabel = overlay.querySelector('.vos-bis-label');
      variantLabel.textContent = 'Variant:';
    }

    if (c.variants && c.variants.length > 1) {
      c.unavailable.forEach(function (v) {
        var opt = document.createElement('option');
        opt.value = v.id;
        opt.textContent = v.title;
        variantSel.appendChild(opt);
      });
    }

    overlay.style.setProperty('--vos-text', textColor);
    overlay.style.setProperty('--vos-btn-bg', buttonBg);
    overlay.style.setProperty('--vos-btn-text', buttonText);
    overlay.style.setProperty('--vos-modal-bg', modal.background_color || '#ffffff');
    overlay.style.setProperty('--vos-filter', modal.drop_background_color || '#000000');
    overlay.style.setProperty('--vos-close', modal.close_button_color || '#cccccc');
    overlay.style.setProperty('--vos-error-bg', modal.error_background_color || '#fcd6d7');
    overlay.style.setProperty('--vos-error-text', modal.error_text_color || '#C72E2F');
    overlay.style.setProperty('--vos-success-bg', modal.success_background_color || '#d3efcd');
    overlay.style.setProperty('--vos-success-text', modal.success_text_color || '#1B9500');

    function hexToRgb(hex) {
      var h = (hex || '').replace('#', '');
      if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
      if (h.length !== 6) return '0,0,0';
      var n = parseInt(h, 16);
      return ((n >> 16) & 255) + ',' + ((n >> 8) & 255) + ',' + (n & 255);
    }
    overlay.style.setProperty('--vos-filter-rgb', hexToRgb(modal.drop_background_color));

    function closeModal() { overlay.remove(); }
    close.addEventListener('click', closeModal);
    overlay.addEventListener('click', function (e) { if (e.target === overlay) closeModal(); });

    var variantId = c.variants.length === 1 ? c.variants[0].id : (variantSel ? Number(variantSel.value) : c.variants[0].id);

    function showError(msg) { errorMsg.textContent = msg; errorMsg.style.display = 'block'; }
    function hideError() { errorMsg.style.display = 'none'; }

    overlay.querySelector('.vos-bis-form').addEventListener('submit', function (e) {
      e.preventDefault();
      hideError();
      var email = (emailInput.value || '').trim();
      var phone = phoneInput ? (phoneInput.value || '').trim() : '';
      var dial = countrySel ? countrySel.value : '1';

      var digits = phone.replace(/[^0-9]/g, '');
      var e164 = digits ? '+' + dial + digits : '';

      if (requireOne && !email && !phone) {
        showError('Enter an email or phone number to be notified.');
        return;
      }
      if (email && !/@/.test(email)) {
        showError('Enter a valid email.');
        return;
      }
      if (phone && digits.length < 6) {
        showError('Enter a valid phone number.');
        return;
      }

      variantId = c.variants.length === 1 ? c.variants[0].id : (variantSel ? Number(variantSel.value) : variantId);
      submit.disabled = true;

      var profile = { type: 'profile', attributes: {} };
      if (email) profile.attributes.email = email;
      if (phone) profile.attributes.phone_number = e164;
      var channels = [];
      if (email) channels.push('EMAIL');
      if (phone) channels.push('SMS');

      var payload = {
        data: {
          type: 'back-in-stock-subscription',
          attributes: {
            profile: { data: profile },
            channels: channels
          },
          relationships: {
            variant: { data: { type: 'catalog-variant', id: '$shopify:::$default:::' + variantId } }
          }
        }
      };

      var consentDone = function () {
        fetch('https://' + cfg().apiHostname + '/client/back-in-stock-subscriptions/?company_id=' + companyId(), {
          method: 'POST',
          headers: {
            'Revision': '2024-02-15',
            'Content-Type': 'application/json',
            'X-Klaviyo-Onsite': '1'
          },
          body: JSON.stringify(payload)
        }).then(function (res) {
          if (res.ok) {
            submit.style.display = 'none';
            successMsg.textContent = (modal.subscription_success_label || "You're in! We'll let you know when it's back.").replace('{product_name}', c.title);
            successMsg.style.display = 'block';
          } else {
            submit.disabled = false;
            showError('Something went wrong. Please try again.');
          }
        }).catch(function () {
          submit.disabled = false;
          showError('Something went wrong. Please try again.');
        });
      };

      // If SMS consent is captured and an SMS list is configured, subscribe the
      // profile to that list first, then create the BIS subscription either way.
      if (phone && modal.sms_consent_list_id) {
        var consentPayload = {
          data: {
            type: 'subscription',
            attributes: {
              profile: { data: profile },
              custom_source: 'Back in Stock'
            },
            relationships: {
              list: { data: { type: 'list', id: modal.sms_consent_list_id } }
            }
          }
        };
        fetch('https://' + cfg().apiHostname + '/client/subscriptions/?company_id=' + companyId(), {
          method: 'POST',
          headers: {
            'Revision': '2024-02-15',
            'Content-Type': 'application/json',
            'X-Klaviyo-Onsite': '1'
          },
          body: JSON.stringify(consentPayload)
        }).then(consentDone).catch(consentDone);
      } else {
        consentDone();
      }
    });
  }

  /**
   * Reads the product JSON, finds unavailable variants, and opens the modal with
   * just those variants selectable.
   */
  function buildModal(title, allVariants) {
    var c = cfg().config;
    var unavailable = allVariants.filter(function (v) { return v.available === false; });
    if (unavailable.length === 0) return;
    openModal({
      title: title,
      variants: allVariants,
      unavailable: unavailable,
      modal: c.modal
    });
  }

  /**
   * Global delegation: ANY element with .klaviyo-bis-trigger and a
   * data-klaviyo-handle opens the custom modal. Works for liquid-rendered
   * triggers, Rebuy, Searchanise, and any injected trigger.
   *
   * Registered in the CAPTURE phase (3rd arg true): Rebuy attaches a
   * bubble-phase handler on the recommendation card (navigates to the product /
   * intercepts clicks) that calls stopPropagation(), which would otherwise
   * swallow the click before our document-level listener ever ran and the modal
   * would never open. Capturing first lets us handle the trigger and then
   * stopPropagation() so neither Rebuy nor Snize navigates away.
   */
  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t || !t.classList || !t.classList.contains('klaviyo-bis-trigger')) return;
    if (!t.hasAttribute('data-klaviyo-handle')) return;
    e.preventDefault();
    e.stopPropagation();
    var handle = t.getAttribute('data-klaviyo-handle');
    fetch(productDataUrl(handle), { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (p) {
        buildModal(p.title, p.variants || []);
      })
      .catch(function () {});
  }, true);

  var bisScanTimer = null;
  function scheduleBisScan() {
    if (bisScanTimer) clearTimeout(bisScanTimer);
    bisScanTimer = setTimeout(scanBis, 150);
  }

  /** Single scan entry point shared by the Searchanise hooks AND the observer. */
  function scanBis() {
    injectRebuyBis();
    injectSnizeBis();
  }

  /**
   * Searchanise fires custom events after it renders/updates results. We hook
   * both Searchanise.Loaded and Searchanise.ResultsUpdated (the latter also
   * fires on pagination, sorting, filter changes and resizes) to re-run the scan.
   * Uses the same scanBis() function referenced by the observer below.
   */
  function setupSearchaniseHooks() {
    if (typeof window.Searchanise === 'undefined') {
      window.Searchanise = {};
    }
    var addHook = function () {
      if (window.jQuery) {
        try {
          window.jQuery(document).on('Searchanise.ResultsUpdated', function (event, container) {
            scheduleBisScan();
          });
          window.jQuery(document).on('Searchanise.Loaded', function () {
            scheduleBisScan();
          });
          return true;
        } catch (err) {
          return false;
        }
      }
      return false;
    };
    if (!addHook()) {
      if (document.addEventListener) {
        document.addEventListener('Searchanise.ResultsUpdated', function () {
          scheduleBisScan();
        });
        document.addEventListener('Searchanise.Loaded', function () {
          scheduleBisScan();
        });
      }
    }
  }

  /**
   * Safety-net observer: re-runs the same scanBis() when the DOM changes, so the
   * notify-me button is injected even if Searchanise's own events are missed.
   * Debounced via scheduleBisScan.
   */
  function setupObserver() {
    if (typeof MutationObserver !== 'undefined') {
      var observer = new MutationObserver(function (mutations) {
        var shouldScan = false;
        for (var i = 0; i < mutations.length; i++) {
          if (mutations[i].addedNodes.length > 0) { shouldScan = true; break; }
        }
        if (shouldScan) scheduleBisScan();
      });
      observer.observe(document.body, { childList: true, subtree: true });
    }
  }

  function onReady() {
    applyCollectionButtonText();
    applyProductButtonText();
    setupSearchaniseHooks();
    setupObserver();
    scanBis();
    // Staggered rescans in case Searchanise/Rebuy render slightly later.
    setTimeout(scanBis, 500);
    setTimeout(scanBis, 1500);
    setTimeout(scanBis, 3000);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', onReady);
  } else {
    onReady();
  }
})();
